import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import {
  EntryRule,
  UpdateEntryRuleDto,
  ManualConfirmedPair,
  ENTRY_HARD_CAP_GROSS,
  EnrichedEntryStatus,
  MakerFlag
} from '../../types/entry.types';
import {
  allInKalshiAsk,
  allInPolymarketAsk,
  KALSHI_TAKER_FEE_RATE,
  polymarketTakerFeeRate,
  takerTnaFromSum,
  fmtPrice,
  fmtQty,
  fmtPct,
  POLY_TICK,
  KALSHI_TICK,
  TAB_FLAGS,
  FLAG_TO_SIDES
} from './entryMath';

/**
 * Per-input save affordance shared by Threshold / Size / MaxV cells.
 * Renders nothing when the field is clean and not mid-save so the cell
 * stays compact at rest. Each input gets its own copy so the operator
 * sees the tick next to the exact field they edited.
 */
function renderSaveIndicator(
  saving: boolean,
  dirty: boolean,
  onClick: () => void,
): JSX.Element | null {
  if (saving && dirty) return <CircularProgress size={12} sx={{ ml: 0.2 }} />;
  if (saving) return null;
  if (!dirty) return null;
  return (
    <IconButton
      size="small"
      onClick={onClick}
      aria-label="save"
      sx={{ p: 0.1, ml: 0.2, color: 'success.main' }}
    >
      <CheckIcon sx={{ fontSize: '0.8rem' }} />
    </IconButton>
  );
}

const hSx = {
  fontSize: '0.58rem',
  fontWeight: 600,
  color: 'grey.500',
  py: 0.4,
  px: 0.2,
  lineHeight: 1.2,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.02em',
  whiteSpace: 'nowrap' as const,
  borderBottom: '1px solid rgba(255,255,255,0.06)'
};

// Sticky offset for the second header row. Must equal row 1's actual
// rendered height in pixels, otherwise the second row either covers
// part of row 1 (overlap) or floats over the body (gap → first body
// row scrolls behind it). With `py: 0.4` + fontSize 0.58rem +
// lineHeight 1.2 + 1px border the row is ~21px.
const ROW1_HEIGHT = 21;

// Second-header-row cells get extra bottom padding + a stronger
// divider so there's visible breathing room between the sub-header
// and the first body row. Bump pb if the operator reports the body
// row 1 still looks crowded against the sub-header.
const subHSx = {
  pb: 1.2,
  borderBottom: '2px solid rgba(255,255,255,0.18)',
};
const gSx = {
  ...hSx,
  textAlign: 'center' as const,
  borderBottom: '1px solid rgba(255,255,255,0.1)'
};
const cSx = { fontSize: '0.72rem', py: 0.15, px: 0.15 } as const;

export interface CreateEntrySumRuleParams {
  pair: ManualConfirmedPair;
  /**
   * Which of the 4 maker flags this row activated on. Drives the initial
   * path. Null when activation came from TT or a pre-rule paused-create
   * via the param ✓.
   */
  makerFlag: MakerFlag | null;
  /**
   * Optional initial strategy. Defaults to 'maker-first' on the backend.
   * Set to 'taker-taker' when the row activates via the TT switch with
   * no maker flag set.
   */
  entryStrategy?: 'maker-first' | 'taker-taker';
  /**
   * Path of the tab the activation happened in. Drives the rule's poly /
   * kalshi sides when `makerFlag` is null (TT-only or paused-create from
   * params). Ignored when `makerFlag` is set — sides derive from the flag
   * via FLAG_TO_SIDES.
   */
  tabPath: 'a' | 'b';
  thresholdSum: number;
  size: number;
  maxVolumeUsd: number;
}

interface Props {
  /**
   * Which entry path this tab governs. Tab A = poly YES + kalshi NO;
   * Tab B = poly NO + kalshi YES.
   */
  path: 'a' | 'b';
  pairs: ManualConfirmedPair[];
  rules: EntryRule[] | undefined;
  anyRulesByPairId: Map<string, EntryRule>;
  getState: (id: string) => EnrichedEntryStatus | undefined;
  /**
   * Pair IDs that have an ACTIVE rule on the OTHER path (or contracts
   * held on the other path). All 3 switches on those rows are disabled
   * here.
   */
  lockedPairIds: Set<string>;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[],
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

const DEFAULT_THRESHOLD = 0.95;
const DEFAULT_SIZE = 50;
const DEFAULT_MAX_VOL = 500;

type SortKey =
  | 'name'
  | 'sumTT'
  | 'tnaTT'
  | 'sumPolyMk'
  | 'tnaPolyMk'
  | 'sumKalshiMk'
  | 'tnaKalshiMk';
type SortDir = 'asc' | 'desc';

// Sums sort ascending by default (cheapest combined cost first); TNAs
// sort descending by default (highest annualized return first).
const SUM_KEYS: SortKey[] = ['sumTT', 'sumPolyMk', 'sumKalshiMk'];

/* ------------------------------------------------------------------ */
/* Math helpers — per-pair                                            */
/* ------------------------------------------------------------------ */

interface PathMetrics {
  polyAsk: number | null;
  polyBid: number | null;
  kalshiAsk: number | null;
  kalshiBid: number | null;
  polyAllIn: number | null;
  kalshiAllIn: number | null;
  polyMakerRest: number | null;
  kalshiMakerRest: number | null;
  sumTT: number | null;
  sumPolyMk: number | null;
  sumKalshiMk: number | null;
  tnaTT: number | null;
  tnaPolyMk: number | null;
  tnaKalshiMk: number | null;
}

const computePathMetrics = (
  path: 'a' | 'b',
  liveState: EnrichedEntryStatus | undefined,
  category: string | undefined,
): PathMetrics => {
  // Raw top-of-book bid/ask for the tab's path (rendered in the
  // platform Bid/Ask cells). Always client-side from WS quotes.
  const polyAsk =
    (path === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk)?.price ?? null;
  const polyBid =
    (path === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid)?.price ?? null;
  const kalshiAsk =
    (path === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk)?.price ?? null;
  const kalshiBid =
    (path === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid)?.price ?? null;

  // Strategy sums + maker rest prices: prefer the engine-parity numbers
  // the backend computes from the SAME helpers the engine triggers
  // on (depth-walked VWAPs + findBestMakerPrice). Fall back to
  // client-side approximations only when the backend doesn't ship the
  // payload (older envelope / orderbook missing entirely).
  const engine = liveState?.engineSim?.[path];
  const polyAllIn =
    engine?.polyTakerAllInVwap ??
    (polyAsk != null ? allInPolymarketAsk(polyAsk, category) : null);
  const kalshiAllIn =
    engine?.kalshiTakerAllInVwap ??
    (kalshiAsk != null ? allInKalshiAsk(kalshiAsk) : null);
  const polyMakerRest =
    engine?.polyMakerRest ?? (polyBid != null ? polyBid + POLY_TICK : null);
  const kalshiMakerRest =
    engine?.kalshiMakerRest ?? (kalshiBid != null ? kalshiBid + KALSHI_TICK : null);

  const sumTT =
    engine?.sumTT ??
    (polyAllIn != null && kalshiAllIn != null ? polyAllIn + kalshiAllIn : null);
  const sumPolyMk =
    engine?.sumPolyMk ??
    (polyMakerRest != null && kalshiAllIn != null
      ? polyMakerRest + kalshiAllIn
      : null);
  const sumKalshiMk =
    engine?.sumKalshiMk ??
    (kalshiMakerRest != null && polyAllIn != null
      ? kalshiMakerRest + polyAllIn
      : null);

  const days = liveState?.daysToExpiry ?? null;
  return {
    polyAsk,
    polyBid,
    kalshiAsk,
    kalshiBid,
    polyAllIn,
    kalshiAllIn,
    polyMakerRest,
    kalshiMakerRest,
    sumTT,
    sumPolyMk,
    sumKalshiMk,
    tnaTT: takerTnaFromSum(sumTT, days),
    tnaPolyMk: takerTnaFromSum(sumPolyMk, days),
    tnaKalshiMk: takerTnaFromSum(sumKalshiMk, days),
  };
};

/* ------------------------------------------------------------------ */
/* Table                                                              */
/* ------------------------------------------------------------------ */

export const EntryTabByPath = ({
  path,
  pairs,
  rules,
  anyRulesByPairId,
  getState,
  lockedPairIds,
  onCreateRule,
  onSetMakerFlag,
  onUpdateRule,
  onHidePair,
}: Props) => {
  const [sortKey, setSortKey] = useState<SortKey>('tnaTT');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rulesByPairId = useMemo(() => {
    const map = new Map<string, EntryRule>();
    for (const rule of rules ?? []) {
      if (!rule.pairId) continue;
      map.set(rule.pairId, rule);
    }
    return map;
  }, [rules]);

  const handleSortClick = useCallback(
    (key: SortKey) => {
      if (sortKey === key) {
        setSortDir(d => (d === 'desc' ? 'asc' : 'desc'));
      } else {
        setSortKey(key);
        // Sums: ascending default (cheapest first). TNAs: descending
        // default (highest yield first). Name: ascending.
        if (key === 'name') setSortDir('asc');
        else if (SUM_KEYS.includes(key)) setSortDir('asc');
        else setSortDir('desc');
      }
    },
    [sortKey],
  );

  // Compute per-pair sort metric. Each sort key maps to ONE specific
  // sum or TNA value from the metrics — no more cross-strategy min/max.
  const sortedPairs = useMemo(() => {
    const annotated = pairs.map(pair => {
      const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
      const liveState =
        (pair._id ? getState(pair._id) : undefined) ??
        (rule ? getState(rule._id) : undefined);
      const m = computePathMetrics(path, liveState, rule?.category);
      const hasSim =
        m.sumTT != null || m.sumPolyMk != null || m.sumKalshiMk != null;
      let metric: number | null = null;
      switch (sortKey) {
        case 'sumTT': metric = m.sumTT; break;
        case 'tnaTT': metric = m.tnaTT; break;
        case 'sumPolyMk': metric = m.sumPolyMk; break;
        case 'tnaPolyMk': metric = m.tnaPolyMk; break;
        case 'sumKalshiMk': metric = m.sumKalshiMk; break;
        case 'tnaKalshiMk': metric = m.tnaKalshiMk; break;
        default: metric = null;
      }
      return { pair, metric, hasSim };
    });
    annotated.sort((a, b) => {
      if (sortKey === 'name') {
        const an = a.pair.pairName ?? '';
        const bn = b.pair.pairName ?? '';
        return sortDir === 'asc' ? an.localeCompare(bn) : bn.localeCompare(an);
      }
      if (a.hasSim !== b.hasSim) return a.hasSim ? -1 : 1;
      if (a.metric == null && b.metric == null) return 0;
      if (a.metric == null) return 1;
      if (b.metric == null) return -1;
      return sortDir === 'desc' ? b.metric - a.metric : a.metric - b.metric;
    });
    return annotated.map(x => x.pair);
  }, [pairs, rulesByPairId, getState, sortKey, sortDir, path]);

  if (pairs.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <Typography sx={{ color: 'grey.500', fontSize: '0.9rem' }}>
          No manually confirmed pairs found.
        </Typography>
      </Box>
    );
  }

  const sortLabelSx = {
    '&.MuiTableSortLabel-root': { color: 'inherit' },
    '&.Mui-active': { color: '#42a5f5' },
    '& .MuiTableSortLabel-icon': { fontSize: '0.8rem' },
    // Show the direction arrow on EVERY sortable label, even when
    // inactive — dimmed at 40% so the active sort still pops. Lets
    // the operator see all sortable handles at a glance instead of
    // only the currently-active one.
    '&:not(.Mui-active) .MuiTableSortLabel-icon': {
      opacity: 0.4,
    },
  } as const;

  const polySideLabel = path === 'a' ? 'YES' : 'NO';
  const kalshiSideLabel = path === 'a' ? 'NO' : 'YES';

  return (
    <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
      <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ ...gSx, width: 260, top: 0, backgroundColor: '#1a1a2e' }}
              rowSpan={2}
            >
              <TableSortLabel
                active={sortKey === 'name'}
                direction={sortKey === 'name' ? sortDir : 'asc'}
                onClick={() => handleSortClick('name')}
                sx={sortLabelSx}
              >
                Market
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={4}
            >
              Polymarket {polySideLabel}
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={4}
            >
              Kalshi {kalshiSideLabel}
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={3}
            >
              Parameters
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              title="Polymarket taker (orange, raw ask) / maker rest (blue)."
            >
              P T/M
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              title="Kalshi taker (orange, raw ask) / maker rest (blue)."
            >
              K T/M
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              title="Taker-Taker: poly + kalshi all-in (with fees)."
            >
              TT
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              title="Poly maker: poly_maker_rest + kalshi_taker_all_in."
            >
              P Mk
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              title="Kalshi maker: kalshi_maker_rest + poly_taker_all_in."
            >
              K Mk
            </TableCell>
            <TableCell
              sx={{ ...gSx, top: 0, backgroundColor: '#1a1a2e' }}
              colSpan={3}
            >
              Ctrl
            </TableCell>
          </TableRow>
          <TableRow>
            {/* Polymarket — Qty · Bid · Ask · Qty */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Bid</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Ask</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            {/* Kalshi — Qty · Bid · Ask · Qty */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Bid</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 32, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Ask</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 24, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Qty</TableCell>
            {/* Parameters */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 40, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Thr</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 30, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">Size</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 36, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="right">MaxV</TableCell>
            {/* Sim — P T/M and K T/M have empty sub-row (no sort);
                strategy columns carry the $/% sort handles only —
                the strategy name (TT / P Mk / K Mk) lives in the
                group header row above so the labels render in full. */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 44, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} />
            <TableCell sx={{ ...hSx, ...subHSx, width: 44, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} />
            {(
              [
                { sumKey: 'sumTT',       tnaKey: 'tnaTT',       tip: 'Taker-Taker — click $ to sort by Sum, % to sort by TNA.' },
                { sumKey: 'sumPolyMk',   tnaKey: 'tnaPolyMk',   tip: 'Poly maker — click $ to sort by Sum, % to sort by TNA.' },
                { sumKey: 'sumKalshiMk', tnaKey: 'tnaKalshiMk', tip: 'Kalshi maker — click $ to sort by Sum, % to sort by TNA.' },
              ] as const
            ).map(({ sumKey, tnaKey, tip }) => (
              <TableCell
                key={sumKey}
                sx={{ ...hSx, ...subHSx, width: 50, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }}
                align="right"
                title={tip}
              >
                <Stack
                  direction="row"
                  spacing={0.4}
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  <TableSortLabel
                    active={sortKey === sumKey}
                    direction={sortKey === sumKey ? sortDir : 'asc'}
                    onClick={() => handleSortClick(sumKey)}
                    sx={sortLabelSx}
                  >
                    $
                  </TableSortLabel>
                  <TableSortLabel
                    active={sortKey === tnaKey}
                    direction={sortKey === tnaKey ? sortDir : 'desc'}
                    onClick={() => handleSortClick(tnaKey)}
                    sx={sortLabelSx}
                  >
                    %
                  </TableSortLabel>
                </Stack>
              </TableCell>
            ))}
            {/* Ctrl — 3 switches: PolyMk · KalshiMk · TT */}
            <TableCell sx={{ ...hSx, ...subHSx, width: 28, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="center" title="Poly maker">P</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 28, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="center" title="Kalshi maker">K</TableCell>
            <TableCell sx={{ ...hSx, ...subHSx, width: 28, top: ROW1_HEIGHT, backgroundColor: '#1a1a2e' }} align="center" title="Taker-Taker">TT</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPairs.map(pair => {
            const rule = pair._id ? rulesByPairId.get(pair._id) : undefined;
            const anyRule = pair._id ? anyRulesByPairId.get(pair._id) : undefined;
            const liveState =
              (pair._id ? getState(pair._id) : undefined) ??
              (rule ? getState(rule._id) : undefined);
            const lockedByOtherTab = pair._id
              ? lockedPairIds.has(pair._id)
              : false;
            return (
              <EntryRowByPath
                key={pair._id ?? pair.pairName}
                path={path}
                pair={pair}
                rule={rule}
                anyRule={anyRule}
                liveState={liveState}
                lockedByOtherTab={lockedByOtherTab}
                onCreateRule={onCreateRule}
                onSetMakerFlag={onSetMakerFlag}
                onUpdateRule={onUpdateRule}
                onHidePair={onHidePair}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

/* ------------------------------------------------------------------ */
/* Row                                                                */
/* ------------------------------------------------------------------ */

interface RowProps {
  path: 'a' | 'b';
  pair: ManualConfirmedPair;
  rule?: EntryRule;
  anyRule?: EntryRule;
  liveState?: EnrichedEntryStatus;
  lockedByOtherTab: boolean;
  onCreateRule: (params: CreateEntrySumRuleParams) => Promise<void>;
  onSetMakerFlag: (
    rule: EntryRule,
    flag: MakerFlag,
    value: boolean,
    clearOthers?: MakerFlag[],
  ) => Promise<void> | void;
  onUpdateRule: (rule: EntryRule, dto: UpdateEntryRuleDto) => Promise<boolean>;
  onHidePair: (pair: ManualConfirmedPair) => Promise<void> | void;
}

const EntryRowByPath = memo(
  ({
    path,
    pair,
    rule,
    anyRule,
    liveState,
    lockedByOtherTab,
    onCreateRule,
    onSetMakerFlag,
    onUpdateRule,
    onHidePair,
  }: RowProps) => {
    const ruleSize =
      rule?.kalshiConfig?.contractsToBuy ??
      rule?.polymarketConfig?.contractsToBuy ??
      DEFAULT_SIZE;
    const initThreshold =
      rule?.thresholdSum != null ? String(rule.thresholdSum) : String(DEFAULT_THRESHOLD);
    const initSize = String(ruleSize);
    const initMaxVol = String(rule?.maxVolumeUsd ?? DEFAULT_MAX_VOL);

    const [threshold, setThreshold] = useState(initThreshold);
    const [size, setSize] = useState(initSize);
    const [maxVol, setMaxVol] = useState(initMaxVol);
    const [savedThreshold, setSavedThreshold] = useState(initThreshold);
    const [savedSize, setSavedSize] = useState(initSize);
    const [savedMaxVol, setSavedMaxVol] = useState(initMaxVol);
    const [thresholdError, setThresholdError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [togglingFlag, setTogglingFlag] = useState<MakerFlag | null>(null);
    const [togglingTT, setTogglingTT] = useState(false);
    const [hidePending, setHidePending] = useState(false);

    useEffect(() => {
      if (!rule) return;
      const dirty =
        threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;
      if (dirty) return;
      if (rule.thresholdSum != null) {
        const next = String(rule.thresholdSum);
        if (next !== savedThreshold) {
          setThreshold(next);
          setSavedThreshold(next);
        }
      }
      const ruleSizeNow =
        rule.kalshiConfig?.contractsToBuy ?? rule.polymarketConfig?.contractsToBuy;
      if (ruleSizeNow != null) {
        const next = String(ruleSizeNow);
        if (next !== savedSize) {
          setSize(next);
          setSavedSize(next);
        }
      }
      const nextMax = String(rule.maxVolumeUsd);
      if (nextMax !== savedMaxVol) {
        setMaxVol(nextMax);
        setSavedMaxVol(nextMax);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      rule?.thresholdSum,
      rule?.maxVolumeUsd,
      rule?.kalshiConfig?.contractsToBuy,
      rule?.polymarketConfig?.contractsToBuy,
    ]);

    const isDirty =
      threshold !== savedThreshold || size !== savedSize || maxVol !== savedMaxVol;

    const validateThreshold = useCallback((value: string): string | null => {
      const n = Number.parseFloat(value);
      if (!Number.isFinite(n) || n <= 0) return 'Must be > 0';
      if (n >= 1) return 'Must be < 1.00 (hard cap)';
      return null;
    }, []);

    useEffect(() => {
      setThresholdError(validateThreshold(threshold));
    }, [threshold, validateThreshold]);

    const handleSave = useCallback(async () => {
      if (!isDirty || !rule) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThreshold = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxVol = Number.parseFloat(maxVol);
      const dto: UpdateEntryRuleDto = {};
      if (
        Number.isFinite(parsedThreshold) &&
        parsedThreshold > 0 &&
        parsedThreshold < 1 &&
        threshold !== savedThreshold
      ) {
        dto.thresholdSum = parsedThreshold;
      }
      if (Number.isFinite(parsedMaxVol) && parsedMaxVol > 0 && maxVol !== savedMaxVol) {
        dto.maxVolumeUsd = parsedMaxVol;
      }
      if (Number.isFinite(parsedSize) && parsedSize > 0 && size !== savedSize) {
        if (rule.kalshiConfig) {
          dto.kalshiConfig = {
            side: rule.kalshiConfig.side,
            ticker: rule.kalshiConfig.ticker,
            contractsToBuy: parsedSize,
          };
        }
        if (rule.polymarketConfig) {
          dto.polymarketConfig = {
            side: rule.polymarketConfig.side,
            yesTokenId: rule.polymarketConfig.yesTokenId,
            noTokenId: rule.polymarketConfig.noTokenId,
            conditionId: rule.polymarketConfig.conditionId,
            contractsToBuy: parsedSize,
          };
        }
      }
      setSaving(true);
      const ok = await onUpdateRule(rule, dto);
      if (ok) {
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
      }
      setSaving(false);
    }, [
      isDirty,
      threshold,
      size,
      maxVol,
      savedThreshold,
      savedSize,
      savedMaxVol,
      rule,
      onUpdateRule,
      validateThreshold,
    ]);

    // Pre-rule commit: typing values with no rule creates a PAUSED rule
    // carrying those values. No maker flag is set — the row lands paused
    // with all 3 switches visually off (rule flags are all false on
    // creation). The operator later activates via a switch.
    const handleCreatePausedRule = useCallback(async () => {
      if (!isDirty || rule || !pair._id) return;
      const tErr = validateThreshold(threshold);
      if (tErr) {
        setThresholdError(tErr);
        return;
      }
      const parsedThr = Number.parseFloat(threshold);
      const parsedSize = Number.parseInt(size, 10);
      const parsedMaxV = Number.parseFloat(maxVol);
      if (!Number.isFinite(parsedThr) || parsedThr <= 0 || parsedThr >= 1) return;
      if (!Number.isFinite(parsedSize) || parsedSize <= 0) return;
      if (!Number.isFinite(parsedMaxV) || parsedMaxV <= 0) return;
      setSaving(true);
      try {
        await onCreateRule({
          pair,
          makerFlag: null,
          tabPath: path,
          thresholdSum: parsedThr,
          size: parsedSize,
          maxVolumeUsd: parsedMaxV,
        });
        setSavedThreshold(threshold);
        setSavedSize(size);
        setSavedMaxVol(maxVol);
      } finally {
        setSaving(false);
      }
    }, [
      isDirty,
      rule,
      pair,
      path,
      threshold,
      size,
      maxVol,
      validateThreshold,
      onCreateRule,
    ]);

    const handleCommit = useCallback(async () => {
      if (rule) {
        await handleSave();
      } else {
        await handleCreatePausedRule();
      }
    }, [rule, handleSave, handleCreatePausedRule]);

    // Path of the existing rule (if any). Drives which switches are bound
    // to live state vs forced to off.
    const committedPath = liveState?.committedPath ?? null;
    const rulePathFromFlags: 'a' | 'b' | null = rule
      ? rule.polyYesMaker || rule.kalshiNoMaker
        ? 'a'
        : rule.polyNoMaker || rule.kalshiYesMaker
          ? 'b'
          : null
      : null;
    const rulePathFromSides: 'a' | 'b' | null = rule
      ? rule.polymarketConfig?.side === 'yes' && rule.kalshiConfig?.side === 'no'
        ? 'a'
        : rule.polymarketConfig?.side === 'no' && rule.kalshiConfig?.side === 'yes'
          ? 'b'
          : null
      : null;
    const rulePath: 'a' | 'b' | null =
      committedPath ?? rulePathFromFlags ?? rulePathFromSides;
    const ruleBelongsToThisTab = rulePath == null || rulePath === path;

    // Market data + math — always tracks the TAB's path, never a
    // "lower-sum" hint. The other tab owns the other path.
    const metrics = computePathMetrics(path, liveState, rule?.category);
    const {
      polyAsk,
      polyBid,
      kalshiAsk,
      kalshiBid,
      polyAllIn,
      kalshiAllIn,
      polyMakerRest,
      kalshiMakerRest,
      sumTT,
      sumPolyMk,
      sumKalshiMk,
      tnaTT,
      tnaPolyMk,
      tnaKalshiMk,
    } = metrics;

    const polyAskSize =
      (path === 'a' ? liveState?.polyYesAsk : liveState?.polyNoAsk)?.size;
    const polyBidSize =
      (path === 'a' ? liveState?.polyYesBid : liveState?.polyNoBid)?.size;
    const kalshiAskSize =
      (path === 'a' ? liveState?.kalshiNoAsk : liveState?.kalshiYesAsk)?.size;
    const kalshiBidSize =
      (path === 'a' ? liveState?.kalshiNoBid : liveState?.kalshiYesBid)?.size;

    const thresholdNum = Number.parseFloat(threshold);
    const thresholdValid =
      Number.isFinite(thresholdNum) && thresholdNum > 0 && thresholdNum < 1;

    // Switch → flag mapping for this tab's path.
    const polyFlag = TAB_FLAGS[path].poly;
    const kalshiFlag = TAB_FLAGS[path].kalshi;
    const otherPath: 'a' | 'b' = path === 'a' ? 'b' : 'a';
    const otherPolyFlag = TAB_FLAGS[otherPath].poly;
    const otherKalshiFlag = TAB_FLAGS[otherPath].kalshi;

    // Switch display state: only bind to rule flags if the rule belongs
    // to this tab's path. Off otherwise — the OTHER tab owns it.
    const polyMakerOn = ruleBelongsToThisTab && rule?.[polyFlag] === true;
    const kalshiMakerOn = ruleBelongsToThisTab && rule?.[kalshiFlag] === true;
    const ttOn =
      ruleBelongsToThisTab &&
      (anyRule?.entryStrategy ?? 'maker-first') === 'taker-taker';

    const inSx = {
      '& .MuiInputBase-input': {
        fontSize: '0.62rem',
        py: 0.15,
        px: 0.2,
        textAlign: 'right',
        width: 28,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255,255,255,0.08)',
      },
      minWidth: 0,
    } as const;

    const tooltipTitle = (
      <Box>
        <Typography sx={{ fontSize: '0.7rem' }}>
          {pair.polymarketTitle || pair.pairName}
        </Typography>
        <Typography sx={{ fontSize: '0.65rem', color: 'grey.400' }}>
          {pair.kalshiTitle || pair.pairName}
        </Typography>
      </Box>
    );

    // The red hard-cap row outline is gone. Hover highlight stays.
    const rowSx = { '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)' } };

    // Apply a maker-flag change. Atomically clears the OTHER path's
    // flags when flipping ON, so a paused cross-path rule flips paths
    // cleanly without prompting the operator (the active-cross-path
    // lock has already disabled the switch by this point).
    const doSetMakerFlag = useCallback(
      async (flag: MakerFlag, value: boolean) => {
        setTogglingFlag(flag);
        try {
          if (rule) {
            const clearOthers: MakerFlag[] = value
              ? [otherPolyFlag, otherKalshiFlag]
              : [];
            await onSetMakerFlag(rule, flag, value, clearOthers);
          } else if (value) {
            const tErr = validateThreshold(threshold);
            if (tErr) {
              setThresholdError(tErr);
              return;
            }
            const parsedThr = Number.parseFloat(threshold);
            const parsedSize = Number.parseInt(size, 10);
            const parsedMaxV = Number.parseFloat(maxVol);
            await onCreateRule({
              pair,
              makerFlag: flag,
              tabPath: path,
              thresholdSum:
                Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                  ? parsedThr
                  : DEFAULT_THRESHOLD,
              size:
                Number.isFinite(parsedSize) && parsedSize > 0
                  ? parsedSize
                  : DEFAULT_SIZE,
              maxVolumeUsd:
                Number.isFinite(parsedMaxV) && parsedMaxV > 0
                  ? parsedMaxV
                  : DEFAULT_MAX_VOL,
            });
          }
        } finally {
          setTimeout(() => setTogglingFlag(null), 2000);
        }
      },
      [
        rule,
        threshold,
        size,
        maxVol,
        pair,
        path,
        otherPolyFlag,
        otherKalshiFlag,
        onCreateRule,
        onSetMakerFlag,
        validateThreshold,
      ],
    );

    const handleTTToggle = useCallback(
      async (value: boolean) => {
        if (lockedByOtherTab) return;
        setTogglingTT(true);
        try {
          if (anyRule) {
            // If the rule belongs to the OTHER tab (paused, not
            // committed), we'd need a comprehensive PATCH to switch
            // sides+clear-flags+set-entryStrategy. The lock set already
            // forbids that for active rules. For paused cross-path
            // rules, we send a side-flipping PATCH on TT-on so the
            // operator can switch paths via TT without an extra step.
            if (!ruleBelongsToThisTab && value) {
              const sides = FLAG_TO_SIDES[polyFlag];
              const dto: UpdateEntryRuleDto = {
                entryStrategy: 'taker-taker',
                [otherPolyFlag]: false,
                [otherKalshiFlag]: false,
              };
              if (anyRule.kalshiConfig) {
                dto.kalshiConfig = {
                  side: sides.kalshiSide,
                  ticker: anyRule.kalshiConfig.ticker,
                  contractsToBuy: anyRule.kalshiConfig.contractsToBuy,
                };
              }
              if (anyRule.polymarketConfig) {
                dto.polymarketConfig = {
                  side: sides.polySide,
                  yesTokenId: anyRule.polymarketConfig.yesTokenId,
                  noTokenId: anyRule.polymarketConfig.noTokenId,
                  conditionId: anyRule.polymarketConfig.conditionId,
                  contractsToBuy: anyRule.polymarketConfig.contractsToBuy,
                };
              }
              await onUpdateRule(anyRule, dto);
            } else {
              await onUpdateRule(anyRule, {
                entryStrategy: value ? 'taker-taker' : 'maker-first',
              });
            }
            return;
          }
          if (!value) return;
          const tErr = validateThreshold(threshold);
          if (tErr) {
            setThresholdError(tErr);
            return;
          }
          const parsedThr = Number.parseFloat(threshold);
          const parsedSize = Number.parseInt(size, 10);
          const parsedMaxV = Number.parseFloat(maxVol);
          await onCreateRule({
            pair,
            makerFlag: null,
            tabPath: path,
            entryStrategy: 'taker-taker',
            thresholdSum:
              Number.isFinite(parsedThr) && parsedThr > 0 && parsedThr < 1
                ? parsedThr
                : DEFAULT_THRESHOLD,
            size:
              Number.isFinite(parsedSize) && parsedSize > 0
                ? parsedSize
                : DEFAULT_SIZE,
            maxVolumeUsd:
              Number.isFinite(parsedMaxV) && parsedMaxV > 0
                ? parsedMaxV
                : DEFAULT_MAX_VOL,
          });
        } finally {
          setTimeout(() => setTogglingTT(false), 2000);
        }
      },
      [
        anyRule,
        lockedByOtherTab,
        ruleBelongsToThisTab,
        polyFlag,
        otherPolyFlag,
        otherKalshiFlag,
        pair,
        path,
        threshold,
        size,
        maxVol,
        onCreateRule,
        onUpdateRule,
        validateThreshold,
      ],
    );

    const ruleStatusColor =
      anyRule?.status === 'active'
        ? '#4caf50'
        : anyRule?.status === 'paused'
          ? '#ff9800'
          : 'grey.600';

    // Sum cell color: red if over hard cap (informational only — the
    // backend gate is the safety contract); green if at or below the
    // operator's threshold; default blue otherwise.
    const sumColor = (sum: number | null): string => {
      if (sum == null) return 'grey.500';
      if (sum >= ENTRY_HARD_CAP_GROSS) return '#f44336';
      if (thresholdValid && sum <= thresholdNum) return '#4caf50';
      return '#42a5f5';
    };
    const tnaColor = (tna: number | null): string => {
      if (tna == null) return 'grey.500';
      return tna >= 0 ? '#4caf50' : '#f44336';
    };

    const lockTooltip = lockedByOtherTab
      ? `Locked — same pair has an active rule on the ${
          path === 'a' ? 'B (poly NO + kalshi YES)' : 'A (poly YES + kalshi NO)'
        } path. Switch tabs to manage it.`
      : '';

    const polyCategoryFeePct = (
      polymarketTakerFeeRate(rule?.category) * 100
    ).toFixed(1);

    return (
      <>
        <TableRow sx={rowSx}>
          {/* Market */}
          <TableCell sx={{ ...cSx, overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.2 }}>
              <Tooltip
                title={
                  anyRule
                    ? `Entry rule: ${anyRule.status}`
                    : 'No rule yet — toggle a switch to create'
                }
                placement="top"
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: ruleStatusColor,
                    mt: 0.5,
                    ml: 0.2,
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
              <Tooltip title="Remove pair from opener page" placement="top">
                <span>
                  <IconButton
                    size="small"
                    disabled={hidePending}
                    onClick={async e => {
                      e.stopPropagation();
                      setHidePending(true);
                      try {
                        await onHidePair(pair);
                      } catch {
                        // The mutation's onError rolls the optimistic cache
                        // back; nothing else to do here.
                      } finally {
                        setHidePending(false);
                      }
                    }}
                    sx={{
                      p: 0,
                      ml: 0.4,
                      color: hidePending ? 'grey.700' : 'grey.500',
                      '&:hover': { color: '#f44336' },
                      flexShrink: 0,
                    }}
                  >
                    {hidePending ? (
                      <CircularProgress size={12} />
                    ) : (
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={tooltipTitle} placement="top-start">
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.15,
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <a
                      href={
                        pair.polymarket?.marketUrl ??
                        liveState?.polymarketMarketUrl ??
                        undefined
                      }
                      target="_blank"
                      rel="noopener"
                      onClick={e => e.stopPropagation()}
                    >
                      <img
                        src="/assets/exchanges/png/100x100/polymarket.png"
                        alt="P"
                        style={{ width: 13, height: 13, borderRadius: '50%' }}
                      />
                    </a>
                    <Typography
                      sx={{
                        fontSize: '0.65rem',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pair.polymarketTitle || pair.pairName}
                    </Typography>
                    <Chip
                      label={path === 'a' ? 'YES' : 'NO'}
                      size="small"
                      color={path === 'a' ? 'success' : 'error'}
                      variant="outlined"
                      sx={{
                        fontSize: '0.42rem',
                        height: 12,
                        '& .MuiChip-label': { px: 0.25 },
                        opacity: 0.7,
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                    <a
                      href={
                        pair.kalshi?.marketUrl ??
                        liveState?.kalshiMarketUrl ??
                        undefined
                      }
                      target="_blank"
                      rel="noopener"
                      onClick={e => e.stopPropagation()}
                    >
                      <img
                        src="/assets/exchanges/png/100x100/kalshi.png"
                        alt="K"
                        style={{ width: 13, height: 13, borderRadius: '50%' }}
                      />
                    </a>
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        color: 'grey.400',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {pair.kalshiTitle || pair.pairName}
                    </Typography>
                    <Chip
                      label={path === 'a' ? 'NO' : 'YES'}
                      size="small"
                      color={path === 'a' ? 'error' : 'success'}
                      variant="outlined"
                      sx={{
                        fontSize: '0.42rem',
                        height: 12,
                        '& .MuiChip-label': { px: 0.25 },
                        opacity: 0.7,
                      }}
                    />
                  </Box>
                </Box>
              </Tooltip>
            </Box>
          </TableCell>

          {/* Polymarket — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>
              {fmtPrice(polyBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {fmtPrice(polyAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(polyAskSize)}
            </Typography>
          </TableCell>

          {/* Kalshi — Qty · Bid · Ask · Qty */}
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiBidSize)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', color: 'grey.300' }}>
              {fmtPrice(kalshiBid)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 600 }}>
              {fmtPrice(kalshiAsk)}
            </Typography>
          </TableCell>
          <TableCell sx={cSx} align="right">
            <Typography sx={{ fontSize: '0.6rem', color: 'grey.500' }}>
              {fmtQty(kalshiAskSize)}
            </Typography>
          </TableCell>

          {/* Parameters */}
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Tooltip
              title={thresholdError ?? ''}
              placement="top"
              disableHoverListener={!thresholdError}
            >
              <span>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0}
                  justifyContent="flex-end"
                >
                  <TextField
                    variant="outlined"
                    size="small"
                    value={threshold}
                    placeholder="0.95"
                    onChange={e => setThreshold(e.target.value)}
                    onBlur={handleSave}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleCommit();
                    }}
                    error={!!thresholdError}
                    helperText={thresholdError ?? ''}
                    sx={inSx}
                    inputProps={{ step: 0.01, min: 0, max: 0.999 }}
                  />
                  {renderSaveIndicator(
                    saving,
                    threshold !== savedThreshold,
                    handleCommit,
                  )}
                </Stack>
              </span>
            </Tooltip>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={size}
                placeholder="50"
                onChange={e => setSize(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                sx={inSx}
                inputProps={{ step: 1, min: 1 }}
              />
              {renderSaveIndicator(saving, size !== savedSize, handleCommit)}
            </Stack>
          </TableCell>
          <TableCell sx={cSx} align="right" onClick={e => e.stopPropagation()}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0}
              justifyContent="flex-end"
            >
              <TextField
                variant="outlined"
                size="small"
                value={maxVol}
                placeholder="500"
                onChange={e => setMaxVol(e.target.value)}
                onBlur={handleSave}
                onKeyDown={e => {
                  if (e.key === 'Enter') handleCommit();
                }}
                sx={inSx}
                inputProps={{ step: 100, min: 0 }}
              />
              {renderSaveIndicator(saving, maxVol !== savedMaxVol, handleCommit)}
            </Stack>
          </TableCell>

          {/* Sim group — per-platform taker/maker preview cells.
              Orange = raw taker top-of-book (tooltip shows the all-in
              VWAP at size with fee). Blue = real maker rest the
              engine would post at = floor(threshold − counter_all_in,
              0.001). When engineSim is not available, falls back to
              top-of-book + fee and bid + tick respectively. */}
          {(() => {
            const engineSize = liveState?.engineSim?.sizeUsed;
            const polyTakerNote =
              engineSize != null
                ? `at size ${engineSize} (depth-walked VWAP)`
                : `(top-of-book + fee)`;
            const polyMakerNote =
              liveState?.engineSim?.[path]?.polyMakerRest != null
                ? `engine's actual placement price (floor(threshold − kalshi_taker, 0.001))`
                : `bid + ${POLY_TICK} (client estimate)`;
            const kalshiTakerNote =
              engineSize != null
                ? `at size ${engineSize} (depth-walked VWAP)`
                : `(top-of-book + fee)`;
            const kalshiMakerNote =
              liveState?.engineSim?.[path]?.kalshiMakerRest != null
                ? `engine's actual placement price (floor(threshold − poly_taker, 0.001))`
                : `bid + ${KALSHI_TICK} (client estimate)`;
            return (
              <>
                <TableCell sx={cSx} align="right">
                  <Stack spacing={0} alignItems="flex-end">
                    <Tooltip
                      placement="top"
                      title={
                        polyAsk == null
                          ? ''
                          : `Top-of-book ask: ${fmtPrice(polyAsk)}. All-in (taker) ${polyTakerNote}: ${fmtPrice(
                              polyAllIn,
                            )} (Poly taker fee ${polyCategoryFeePct}%).`
                      }
                      disableHoverListener={polyAsk == null}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#ff9800',
                          cursor: polyAsk == null ? 'default' : 'help',
                        }}
                      >
                        {fmtPrice(polyAsk)}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      placement="top"
                      title={`Poly maker rest: ${fmtPrice(polyMakerRest)} — ${polyMakerNote}.`}
                      disableHoverListener={polyMakerRest == null}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#42a5f5',
                          fontWeight: 600,
                          cursor: polyMakerRest == null ? 'default' : 'help',
                        }}
                      >
                        {fmtPrice(polyMakerRest)}
                      </Typography>
                    </Tooltip>
                  </Stack>
                </TableCell>
                <TableCell sx={cSx} align="right">
                  <Stack spacing={0} alignItems="flex-end">
                    <Tooltip
                      placement="top"
                      title={
                        kalshiAsk == null
                          ? ''
                          : `Top-of-book ask: ${fmtPrice(kalshiAsk)}. All-in (taker) ${kalshiTakerNote}: ${fmtPrice(
                              kalshiAllIn,
                            )} (Kalshi taker fee ${(KALSHI_TAKER_FEE_RATE * 100).toFixed(1)}%).`
                      }
                      disableHoverListener={kalshiAsk == null}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#ff9800',
                          cursor: kalshiAsk == null ? 'default' : 'help',
                        }}
                      >
                        {fmtPrice(kalshiAsk)}
                      </Typography>
                    </Tooltip>
                    <Tooltip
                      placement="top"
                      title={`Kalshi maker rest: ${fmtPrice(kalshiMakerRest)} — ${kalshiMakerNote}.`}
                      disableHoverListener={kalshiMakerRest == null}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          color: '#42a5f5',
                          fontWeight: 600,
                          cursor: kalshiMakerRest == null ? 'default' : 'help',
                        }}
                      >
                        {fmtPrice(kalshiMakerRest)}
                      </Typography>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </>
            );
          })()}

          {/* 3 stacked sum+TNA cells — TT | PolyMk | KalshiMk. Sort
              targets each cell's sum OR TNA independently via the
              two-label header. */}
          {(
            [
              { key: 'tt',       sum: sumTT,       tna: tnaTT,       tip: 'Taker-Taker' },
              { key: 'polyMk',   sum: sumPolyMk,   tna: tnaPolyMk,   tip: 'Poly maker' },
              { key: 'kalshiMk', sum: sumKalshiMk, tna: tnaKalshiMk, tip: 'Kalshi maker' },
            ] as const
          ).map(({ key, sum, tna, tip }) => (
            <TableCell key={key} sx={cSx} align="right">
              <Tooltip title={tip} placement="top">
                <Stack spacing={0} alignItems="flex-end">
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      color: sumColor(sum),
                    }}
                  >
                    {fmtPrice(sum)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.62rem', color: tnaColor(tna) }}
                  >
                    {fmtPct(tna)}
                  </Typography>
                </Stack>
              </Tooltip>
            </TableCell>
          ))}

          {/* Ctrl — Poly maker · Kalshi maker · TT */}
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {togglingFlag === polyFlag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  lockedByOtherTab
                    ? lockTooltip
                    : `Poly ${path === 'a' ? 'YES' : 'NO'} as maker`
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={polyMakerOn}
                    disabled={lockedByOtherTab}
                    onChange={(_, v) => void doSetMakerFlag(polyFlag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {togglingFlag === kalshiFlag ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  lockedByOtherTab
                    ? lockTooltip
                    : `Kalshi ${path === 'a' ? 'NO' : 'YES'} as maker`
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={kalshiMakerOn}
                    disabled={lockedByOtherTab}
                    onChange={(_, v) => void doSetMakerFlag(kalshiFlag, v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
          <TableCell sx={cSx} align="center" onClick={e => e.stopPropagation()}>
            {anyRule && (anyRule.balancingContractsRemaining ?? 0) > 0 ? (
              <Tooltip
                title={`Balancing: ${anyRule.balancingContractsRemaining} contracts resting on ${anyRule.balancingLeg}`}
                placement="top"
              >
                <CircularProgress size={14} />
              </Tooltip>
            ) : togglingTT ? (
              <CircularProgress size={14} />
            ) : (
              <Tooltip
                title={
                  lockedByOtherTab
                    ? lockTooltip
                    : ttOn
                      ? 'Taker-Taker on. Click to switch off.'
                      : 'Taker-Taker: fire BOTH legs as parallel takers when combined cost clears threshold.'
                }
                placement="top"
              >
                <span>
                  <Switch
                    size="small"
                    checked={ttOn}
                    disabled={lockedByOtherTab}
                    onChange={(_, v) => void handleTTToggle(v)}
                  />
                </span>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

      </>
    );
  },
  (prev, next) =>
    prev.path === next.path &&
    prev.pair === next.pair &&
    prev.rule === next.rule &&
    prev.anyRule === next.anyRule &&
    prev.liveState === next.liveState &&
    prev.lockedByOtherTab === next.lockedByOtherTab &&
    prev.onCreateRule === next.onCreateRule &&
    prev.onSetMakerFlag === next.onSetMakerFlag &&
    prev.onUpdateRule === next.onUpdateRule &&
    prev.onHidePair === next.onHidePair,
);
